import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ParticipantSummaryCard = ({ 
  participant, 
  onPaymentMethodSelect = () => {},
  onExportReceipt = () => {},
  onMarkAsPaid = () => {},
  isCurrentUser = false 
}) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const getDisplayName = () => {
    return isCurrentUser ? 'Tú' : participant?.name;
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'processing':
        return 'text-primary bg-primary/10 border-primary/20';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Pagado';
      case 'pending':
        return 'Pendiente';
      case 'processing':
        return 'Procesando';
      default:
        return 'Sin pagar';
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${isCurrentUser ? 'ring-2 ring-primary/20 bg-primary/5' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
            {isCurrentUser ? 'T' : participant?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              {getDisplayName()}
            </h3>
            <p className="text-sm text-text-secondary">
              {participant?.selectedItems?.length} artículos
            </p>
          </div>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getPaymentStatusColor(participant?.paymentStatus)}`}>
          {getPaymentStatusLabel(participant?.paymentStatus)}
        </div>
      </div>
      {/* Items List */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-2">Artículos seleccionados:</h4>
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {participant?.selectedItems?.map((item) => (
            <div key={item?.id} className="flex justify-between text-sm">
              <span className="text-text-secondary">
                {item?.quantity}x {item?.name}
              </span>
              <span className="font-mono text-foreground">
                {formatAmount(item?.totalPrice)}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Cost Breakdown */}
      <div className="space-y-2 mb-4 p-3 bg-muted/30 rounded-lg">
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Subtotal:</span>
          <span className="font-mono text-foreground">{formatAmount(participant?.subtotal)}</span>
        </div>
        {/* IVA ya incluido en precios del ticket */}
        {participant?.taxAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">IVA ({participant?.taxRate}%):</span>
            <span className="font-mono text-foreground">{formatAmount(participant?.taxAmount)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Propina:</span>
          <span className="font-mono text-foreground">{formatAmount(participant?.tipAmount)}</span>
        </div>
        <div className="border-t border-border pt-2">
          <div className="flex justify-between font-semibold">
            <span className="text-foreground">Total:</span>
            <span className="font-mono text-lg text-foreground">{formatAmount(participant?.totalAmount)}</span>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="space-y-2">
        {participant?.paymentStatus === 'unpaid' && (
          <>
            <Button
              variant="default"
              fullWidth
              onClick={() => onPaymentMethodSelect(participant?.id)}
              iconName="CreditCard"
              iconPosition="left"
              iconSize={16}
              className="mb-2"
            >
              Pagar {formatAmount(participant?.totalAmount)}
            </Button>
            
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onExportReceipt(participant?.id)}
                iconName="Download"
                iconPosition="left"
                iconSize={14}
              >
                Exportar
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => onMarkAsPaid(participant?.id)}
                iconName="Check"
                iconPosition="left"
                iconSize={14}
              >
                Marcar pagado
              </Button>
            </div>
          </>
        )}

        {participant?.paymentStatus === 'paid' && (
          <div className="flex items-center justify-center space-x-2 py-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">Pago completado</span>
          </div>
        )}

        {participant?.paymentStatus === 'processing' && (
          <div className="flex items-center justify-center space-x-2 py-2">
            <Icon name="Clock" size={16} className="text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Procesando pago...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantSummaryCard;